import moment from 'moment';

const UTC_DATE_FORMAT = 'YYYY-MM-DD';
const ISO_DATE_PATTERN = /\d{4}-\d{2}-\d{2}/g;

/**
 * How many calendar days the given instant moves when read in UTC instead of local time: `1` late
 * in the day behind UTC (local 2026-10-23 23:32 at -06:00 is 2026-10-24 05:32 UTC), `-1` early in
 * the day ahead of it, `0` for the rest of the day.
 */
export function utcDayShift(at = moment()) {
    const local = moment(at).format(UTC_DATE_FORMAT);
    const utc = moment(at).utc().format(UTC_DATE_FORMAT);

    return moment.utc(utc).diff(moment.utc(local), 'days');
}

export const toUtcDay = (date, shift) => moment(date).add(shift, 'days').format(UTC_DATE_FORMAT);

/**
 * Converts one `{ label, key, value, cmp }` bound of the range. Custom ranges carry the picked day
 * inside the label too (`Start 2026-10-14`), so any date spelled out there is re-written to the
 * converted one — labels on the preset ranges (`Last 7 Days`) hold no date and stay untouched.
 */
function toUtcBound(bound, shift) {
    const value = toUtcDay(bound.value, shift);

    return {
        ...bound,
        value,
        label:
            typeof bound.label === 'string'
                ? bound.label.replace(ISO_DATE_PATTERN, value)
                : bound.label,
    };
}

/**
 * Returns a copy of the filters map whose date range is re-anchored to the UTC calendar.
 *
 * AI Tracking rows are stamped in UTC by BigQuery, while the date filter is built off the viewer's
 * local clock, so a locally picked day does not line up with the stored one. The filter itself has
 * to stay local (it feeds the dropdown labels and local storage), hence the copy: only the payload
 * sent with a report request is converted.
 *
 * Both bounds move by the same whole-day shift — the one the current instant takes when read in
 * UTC — so the range keeps the width the viewer picked instead of being stretched at one end. The
 * shift is read once per call so the two bounds cannot straddle a midnight tick.
 */
export default function toUtcFilterDates(filters) {
    const values = filters?.dates?.values;

    if (!values?.start_date?.value || !values?.end_date?.value) return filters;

    const shift = utcDayShift();

    if (!shift) return filters;

    return {
        ...filters,
        dates: {
            ...filters.dates,
            values: {
                ...values,
                start_date: toUtcBound(values.start_date, shift),
                end_date: toUtcBound(values.end_date, shift),
            },
        },
    };
}
